/**
 * MasterDataConsigneeFormDialog — Create/Edit `consignee`.
 * Compact Sales Contract style dialog. Single-column.
 *
 * `consignee_code` là PK text auto-suggest (CSN<nnn>) — áp /access-suggest:
 *   - New: auto-suggest khi mở + blur-check trùng → tự đổi mã + alert vàng.
 *   - Edit: disable, không suggest/check.
 */
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdConsigneeMutation,
  useLazyCheckMdConsigneeCodeQuery,
  useLazySuggestMdConsigneeCodeQuery,
  useUpdateMdConsigneeMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataConsignee } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataConsignee | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  consignee_code: string
  consignee_name: string
}

const EMPTY: FormState = {
  consignee_code: '',
  consignee_name: '',
}

export default function MasterDataConsigneeFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createConsignee, { isLoading: creating }] = useCreateMdConsigneeMutation()
  const [updateConsignee, { isLoading: updating }] = useUpdateMdConsigneeMutation()
  const [triggerSuggest, suggestState] = useLazySuggestMdConsigneeCodeQuery()
  const [triggerCheck] = useLazyCheckMdConsigneeCodeQuery()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  // Hydrate — chỉ depend [open, mode, initialValue] (access-suggest anti #4).
  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    if (isEdit && initialValue) {
      setForm({
        consignee_code: initialValue.consignee_code ?? '',
        consignee_name: initialValue.consignee_name ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  // New: auto-suggest consignee_code khi mở (lazy, guard chỉ set khi trống).
  useEffect(() => {
    if (!open || isEdit) return
    let cancelled = false
    void triggerSuggest(undefined, false).unwrap().then((res) => {
      if (cancelled) return
      const newVal = (res.consignee_code || '').trim()
      if (!newVal) return
      setForm((prev) => (prev.consignee_code.trim() ? prev : { ...prev, consignee_code: newVal }))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [open, isEdit, triggerSuggest])

  // Dismiss alert khi user sửa code khác mã suggest.
  useEffect(() => {
    if (regenNotice && form.consignee_code !== regenNotice.to) setRegenNotice(null)
  }, [form.consignee_code, regenNotice])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.consignee_code.trim()
  const canSave = !codeEmpty && !loading

  // Blur field PK → check trùng, nếu có thì suggest mã mới (access-suggest).
  const handleCodeBlur = async () => {
    if (isEdit) return
    const val = form.consignee_code.trim()
    if (!val) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheck(val, false).unwrap()
      if (!check.exists) return
      const next = await triggerSuggest(undefined, false).unwrap()
      const newVal = (next.consignee_code || '').trim()
      if (newVal && newVal !== val) {
        setForm((prev) => ({ ...prev, consignee_code: newVal }))
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      /* BE raise lúc Save nếu thật sự trùng — không poison UX */
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const buildBody = () => ({
    consignee_name: form.consignee_name.trim() || null,
  })

  const handleSave = async () => {
    if (precheckBusyRef.current) return // race guard (blur + click Save cùng tick)
    if (codeEmpty) {
      setShowErrors(true)
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateConsignee({ code: initialValue.consignee_code, body: buildBody() }).unwrap()
        snack.success('Consignee updated')
      } else {
        await createConsignee({ consignee_code: form.consignee_code.trim(), ...buildBody() }).unwrap()
        snack.success('Consignee created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      // access-suggest anti #13: duplicate race → auto re-suggest + alert vàng.
      const msg = getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create')
      const lower = msg.toLowerCase()
      if (!isEdit && (lower.includes('exist') || lower.includes('duplicate'))) {
        try {
          const next = await triggerSuggest(undefined, false).unwrap()
          const newVal = (next.consignee_code || '').trim()
          const oldVal = form.consignee_code.trim()
          if (newVal && newVal !== oldVal) {
            setForm((prev) => ({ ...prev, consignee_code: newVal }))
            setRegenNotice({ from: oldVal, to: newVal })
            return
          }
        } catch {
          /* fallthrough → snack error */
        }
      }
      snack.error(msg)
    }
  }

  const codeHelper = isEdit
    ? 'Consignee Code cannot be changed after creation.'
    : precheckBusy
      ? 'Verifying code…'
      : suggestState.isFetching
        ? 'Suggesting next code…'
        : 'Suggested code below; edit if needed.'

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Consignee' : 'New Consignee'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.consignee_code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            Consignee Code <b>{regenNotice.from}</b> was just created — suggested new code{' '}
            <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Consignee Code"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && codeEmpty}
              value={form.consignee_code}
              onChange={(e) => set('consignee_code', e.target.value)}
              onBlur={() => void handleCodeBlur()}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1 }}
              helperText={codeHelper}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Consignee Name"
              size="small"
              fullWidth
              value={form.consignee_name}
              onChange={(e) => set('consignee_name', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
